/** @format */

import React,{useContext,useEffect,useState} from 'react'
import ButtonPrimary from '../../../components/Buttons/ButtonPrimary'
import {deleteUser,getAuth,reauthenticateWithRedirect,signOut,GoogleAuthProvider,ProviderId,reauthenticateWithCredential,EmailAuthProvider,sendSignInLinkToEmail,updateProfile} from 'firebase/auth'
import {useNavigate} from 'react-router-dom'
import Subheading from '../../../components/Text/Subheading'
import ButtonSecondary from '../../../components/Buttons/ButtonSecondary'
import ButtonDanger from '../../../components/Buttons/ButtonDanger'
import Text from '../../../components/Text/Text'
import {createPortalLink,scheduleAccountDeletion,exportUserData} from '../../../helpers/functions'
import ImageCropModal from '../../../components/Popups/ImageCropModal'
import Link from '../../../components/Links/Link'
import HorizontalDivider from '../../../components/Dividers/HorizontalDivider'
import useUserSubscription from '../../../hooks/useUserSubscription'
import usePageTitle from '../../../hooks/usePageTitle'
import {db,storage} from '../../../helpers/firebase'
import analytics from '../../../helpers/analytics'
import UserContext from '../../../contexts/UserContext'
import ErrorContext from '../../../contexts/ErrorContext'
import {logEvent} from 'firebase/analytics'
import {getDownloadURL,ref,uploadBytes,deleteObject} from 'firebase/storage'
import Icon from '../../../components/Text/Icon'
import Credits from '../../../components/Progress/Credits'
import CreditsContext from '../../../contexts/CreditsContext'

export default function Settings() {
    usePageTitle('Settings')

    const [user]=useContext(UserContext)
    const [credits,updateCredits]=useContext(CreditsContext)
    const setError=useContext(ErrorContext)

    const [userPlan,setUserPlan]=useState('free')
    const [isImageLoading,setIsImageLoading]=useState(false)
    const [cropFile,setCropFile]=useState(null)
    const [showDeleteModal,setShowDeleteModal]=useState(false)
    const [deleteConfirmText,setDeleteConfirmText]=useState('')
    const [deletePending,setDeletePending]=useState(false)
    const [exportLoading,setExportLoading]=useState(false)

    const navigate=useNavigate()
    const {getUserPlan}=useUserSubscription()
    const auth=getAuth()

    useEffect(()=>{
        getUserPlan().then(plan=>setUserPlan(plan))
        updateCredits()
    },[user.uid,updateCredits,getUserPlan])

    function handleSignout(e) {
        e.preventDefault()
        signOut(auth).then(()=>{
            logEvent(analytics,'sign_out')
            navigate('/login')
        }).catch(()=>setError('Something went wrong signing you out. Please try again.'))
    }

    function handleSubscription() {
        if(userPlan==='plus'||userPlan==='premium') {
            createPortalLink({returnUrl: window.location.origin,locale: 'auto'}).then(data=>{
                logEvent(analytics,'portal_link_created')
                window.location.assign(data.data.url)
            })
        } else {
            navigate('/pricing')
        }
    }

    // 4.4.4 — Delete old image before uploading new one; 5.3.4 — show crop modal
    const handleUploadImage=async (event) => {
        if(!event.target.files.length) return
        const file=event.target.files[0]
        // Show crop modal first — actual upload happens in handleCropConfirm
        if(file.type.startsWith('image/')&&!['image/gif','image/svg+xml'].includes(file.type)) {
            const supportedTypes=['image/png','image/jpeg','image/jpg']
            if(supportedTypes.includes(file.type)) {
                setCropFile(file)
                return
            }
        }
        const errors=[
            [file.size>1000000,'Image must be less than 1MB'],
            [!file.type.startsWith('image/'),'File must be an image'],
            [['image/gif','image/svg+xml','image/webp','image/tiff','image/bmp'].includes(file.type),`${file.type.split('/')[1].toUpperCase()} images are not supported`]
        ]
        for(const [cond,msg] of errors) if(cond){setError(msg);return}

        setIsImageLoading(true)
        try {
            // Delete previous profile image if it exists
            if(user.photoURL&&user.photoURL.includes('firebasestorage')) {
                const oldRef=ref(storage,`users/${user.uid}/profile.${user.photoURL.split('.').pop().split('?')[0]}`)
                await deleteObject(oldRef).catch(()=>{/* ignore if missing */})
            }
            const ext=file.type.split('/')[1]
            const imageRef=ref(storage,`users/${user.uid}/profile.${ext}`)
            await uploadBytes(imageRef,file)
            const url=await getDownloadURL(imageRef)
            logEvent(analytics,'profile_image_changed')
            await updateProfile(user,{photoURL: url})
        } catch(err) {
            setError('Failed to upload image. Please try again.')
        } finally {
            setIsImageLoading(false)
        }
    }

    // 4.4.3 — Delete account modal flow
    const handleDeleteAccount=async () => {
        if(deleteConfirmText!=='DELETE') return
        setDeletePending(true)
        logEvent(analytics,'delete_account')
        try {
            await deleteUser(auth.currentUser)
            navigate('/login')
        } catch(err) {
            // Re-auth required
            if(auth.currentUser.providerData[0]?.providerId===ProviderId.GOOGLE) {
                await reauthenticateWithRedirect(auth.currentUser,new GoogleAuthProvider())
            } else {
                const actionCodeSettings={url: window.location.origin+'/delete/'+auth.currentUser.uid,handleCodeInApp: true}
                await sendSignInLinkToEmail(auth,auth.currentUser.email,actionCodeSettings)
                setError('Check your email — we sent a link to confirm account deletion.')
                setShowDeleteModal(false)
            }
        } finally {
            setDeletePending(false)
        }
    }

    // 5.3.4 — Upload the cropped image data URL
    const handleCropConfirm=async (dataUrl) => {
        setCropFile(null)
        setIsImageLoading(true)
        try {
            // Convert data URL to Blob
            const res=await fetch(dataUrl)
            const blob=await res.blob()
            if(user.photoURL&&user.photoURL.includes('firebasestorage')) {
                const ext=user.photoURL.split('.').pop().split('?')[0]
                const oldRef=ref(storage,`users/${user.uid}/profile.${ext}`)
                await deleteObject(oldRef).catch(()=>{})
            }
            const imageRef=ref(storage,`users/${user.uid}/profile.png`)
            await uploadBytes(imageRef,blob)
            const url=await getDownloadURL(imageRef)
            await updateProfile(user,{photoURL: url})
        } catch {
            setError('Failed to upload image. Please try again.')
        } finally {
            setIsImageLoading(false)
        }
    }

    // 4.4.2 — Schedule soft delete (30-day grace)
    const handleScheduleDeletion=async () => {
        setDeletePending(true)
        try {
            const res=await scheduleAccountDeletion()
            const date=new Date(res.data.scheduledFor).toLocaleDateString()
            setShowDeleteModal(false)
            setError(`Your account will be permanently deleted on ${date}. Sign in before then to cancel.`)
        } catch {
            setError('Could not schedule deletion. Please try again.')
        } finally {
            setDeletePending(false)
        }
    }

    // 4.4.5 — GDPR data export
    const handleExportData=async () => {
        setExportLoading(true)
        try {
            const res=await exportUserData()
            const blob=new Blob([JSON.stringify(res.data,null,2)],{type:'application/json'})
            const url=URL.createObjectURL(blob)
            const a=document.createElement('a')
            a.href=url
            a.download=`flowsage-data-${new Date().toISOString().slice(0,10)}.json`
            a.click()
            URL.revokeObjectURL(url)
        } catch {
            setError('Data export failed. Please try again.')
        } finally {
            setExportLoading(false)
        }
    }

    return (
        <>
            <Subheading className="w-full pb-1 !text-3xl pt-4 lg:px-6 px-4">Settings</Subheading>
            <div className="flex flex-col items-start justify-start w-full h-full p-2 px-6 m-0 mx-auto mt-8 lg:flex-row">
                <div className='flex items-center justify-center w-full mx-auto lg:w-1/5'>
                    <div className='relative flex flex-col items-center justify-center w-64 p-2 mx-auto'>
                        <img src={user.photoURL} className='object-cover w-64 h-64 shadow-md rounded-3xl ms-clarity-hidden' alt='Profile' />
                        <label htmlFor='upload-photo' className='absolute flex items-center justify-center p-2 px-2 text-2xl rounded-full shadow-md cursor-pointer -right-3 aspect-square bg-light bottom-4'>
                            <Icon iconClassName='pb-1' icon={isImageLoading?'hourglass-split':'pencil'} />
                        </label>
                        <input accept='.png,.jpg,.jpeg' type='file' id="upload-photo" disabled={isImageLoading} onChange={handleUploadImage} className='w-64 h-0 p-2 mt-1 rounded-md opacity-0 pointer-events-none' />
                    </div>
                </div>
                <div className='flex flex-col items-start justify-start p-2 lg:w-4/5'>
                    <div className="box-border flex flex-col items-start justify-start w-full h-full p-2 m-0 !mx-auto lg:px-20">
                        <div className='flex flex-col items-start justify-between w-full my-5 lg:flex-row'>
                            <Subheading className='my-auto font-normal'><span className='font-bold ms-clarity-hidden'>{user.displayName||user.email}</span> ({user.displayName&&user.email})</Subheading>
                        </div>
                        <HorizontalDivider />
                        <div className='flex flex-col items-start justify-between w-full my-5 lg:flex-row'>
                            <Subheading className='my-auto'>{(userPlan==='plus'||userPlan==='premium')? 'Manage your subscription':'Upgrade your account'}</Subheading>
                            <ButtonPrimary onClick={handleSubscription} className='w-full lg:w-64'>{(userPlan==='plus'||userPlan==='premium')? 'Payment Dashboard':'View Pricing'}</ButtonPrimary>
                        </div>
                        {userPlan!=='premium'&&<>
                            <HorizontalDivider />
                            <div className='flex flex-col items-start justify-between w-full my-5 lg:flex-row'>
                                <Subheading className='my-auto'>Your Flowsage AI Credits</Subheading>
                                <Credits plan={userPlan} credits={credits} />
                            </div>
                        </>}
                        <HorizontalDivider />
                        {/* 4.4.5 — Data export */}
                        <div className='flex flex-col items-start justify-between w-full my-5 lg:flex-row'>
                            <div>
                                <Subheading className='pb-0'>Download Your Data</Subheading>
                                <Text className='text-sm text-dark-400'>Export all your account data and projects as JSON (GDPR).</Text>
                            </div>
                            <ButtonSecondary onClick={handleExportData} disabled={exportLoading} className='!w-full lg:!w-64'>
                                {exportLoading?'Exporting…':'Download My Data'}
                            </ButtonSecondary>
                        </div>
                        <HorizontalDivider />
                        {/* 4.4.3 — Account danger zone */}
                        <div className='flex flex-col items-start justify-between w-full my-5 lg:flex-row'>
                            <div>
                                <Subheading className='pb-0'>Your Account</Subheading>
                                <Text className='text-sm text-dark-400'>Sign out or delete your account.</Text>
                            </div>
                            <div className='flex flex-col items-center justify-center w-full gap-2 lg:w-auto lg:flex-row'>
                                <ButtonSecondary className='!w-full lg:!w-64' onClick={handleSignout}>Sign out</ButtonSecondary>
                                <ButtonDanger onClick={()=>setShowDeleteModal(true)} className='!w-full lg:!w-64'>Delete Account</ButtonDanger>
                            </div>
                        </div>
                        <HorizontalDivider />
                        <div className='my-5'>
                            <Text className='font-semibold'>Questions? Email us at <Link to='mailto:team@flowsage.co'>team@flowsage.co</Link></Text>
                        </div>
                    </div>
                </div>
            </div>

            {/* 5.3.4 — Image crop modal */}
            {cropFile&&(
                <ImageCropModal
                    file={cropFile}
                    onConfirm={handleCropConfirm}
                    onCancel={()=>setCropFile(null)}
                />
            )}

            {/* 4.4.3 — Delete Account Modal */}
            {showDeleteModal&&(
                <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/40'>
                    <div className='bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md mx-4'>
                        <div className='flex items-center justify-between mb-4'>
                            <Subheading className='!text-xl !pb-0 text-danger'>Delete Account</Subheading>
                            <button onClick={()=>{setShowDeleteModal(false);setDeleteConfirmText('')}} className='p-1 rounded hover:bg-gray-100 transition-colors'>
                                <Icon icon='x' className='text-xl text-gray-500' />
                            </button>
                        </div>
                        <Text className='mb-4 text-sm text-dark-500'>This will permanently delete your account, all projects, and all data. This cannot be undone.</Text>

                        <div className='flex flex-col gap-3 mb-6 p-3 rounded-lg bg-gray-50 border border-gray-200'>
                            <Text className='text-sm font-semibold'>Type <code className='px-1 py-0.5 bg-red-100 text-red-700 rounded text-xs'>DELETE</code> to confirm:</Text>
                            <input
                                value={deleteConfirmText}
                                onChange={e=>setDeleteConfirmText(e.target.value)}
                                placeholder='DELETE'
                                className='px-3 py-2 text-sm rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-danger'
                            />
                        </div>

                        <div className='flex flex-col gap-2'>
                            <ButtonDanger
                                onClick={handleDeleteAccount}
                                disabled={deleteConfirmText!=='DELETE'||deletePending}
                                className='w-full'
                            >
                                {deletePending?'Deleting…':'Delete immediately'}
                            </ButtonDanger>
                            <ButtonSecondary
                                onClick={handleScheduleDeletion}
                                disabled={deleteConfirmText!=='DELETE'||deletePending}
                                className='w-full !border-orange-300 !text-orange-600 hover:!bg-orange-50'
                            >
                                Schedule deletion in 30 days
                            </ButtonSecondary>
                            <ButtonSecondary onClick={()=>{setShowDeleteModal(false);setDeleteConfirmText('')}} className='w-full'>
                                Cancel
                            </ButtonSecondary>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}
